import axiosInstance from '../api/axiosInstance';
import { sharedRequest } from '@/utils/sharedRequest';
import {
  EXAM_ENDPOINTS,
  MARKING_ENDPOINTS,
  QUESTION_BANK_ENDPOINTS,
} from '@/api/endpoints/assessment.endpoints';
import {
  AttemptDetail,
  BankSummary,
  CourseMarker,
  Exam,
  ExamInfo,
  ExamResult,
  ExamResultsList,
  ExamTakeState,
  ExamUpdate,
  DrawShortfall,
  DrawImpactChange,
  MarkingItem,
  PaginatedQuestions,
  Question,
  QuestionCategory,
  QuestionCreate,
  QuestionListFilters,
  QuestionUpdate,
} from '@/types/assessment.types';

function buildParams(f: QuestionListFilters): Record<string, any> {
  const p: Record<string, any> = {
    limit: f.limit ?? 20,
    offset: f.offset ?? 0,
  };
  if (f.category_id != null) p.category_id = f.category_id;
  if (f.uncategorised) p.uncategorised = true;
  if (f.types && f.types.length) p.types = f.types;
  if (f.difficulty) p.difficulty = f.difficulty;
  if (f.tags && f.tags.length) p.tags = f.tags;
  if (f.q) p.q = f.q;
  if (f.source) p.source = f.source;
  if (f.trashed) p.trashed = true;
  if (f.status) p.status = f.status;
  if (f.sort && f.sort !== 'newest') p.sort = f.sort;
  return p;
}

export const QuestionBankApi = {
  // ── categories ──────────────────────────────────────────────────
  async listCategories(courseId: number): Promise<QuestionCategory[]> {
    const res = await axiosInstance.get<QuestionCategory[]>(
      QUESTION_BANK_ENDPOINTS.CATEGORIES(courseId),
    );
    return res.data;
  },

  async getSummary(courseId: number): Promise<BankSummary> {
    const res = await axiosInstance.get<BankSummary>(
      QUESTION_BANK_ENDPOINTS.SUMMARY(courseId),
    );
    return res.data;
  },

  async createCategory(
    courseId: number,
    payload: { name: string; parent_id?: number | null; order?: number },
  ): Promise<QuestionCategory> {
    const res = await axiosInstance.post<QuestionCategory>(
      QUESTION_BANK_ENDPOINTS.CATEGORIES(courseId),
      payload,
    );
    return res.data;
  },

  async updateCategory(
    courseId: number,
    categoryId: number,
    payload: { name?: string; parent_id?: number | null; order?: number },
  ): Promise<QuestionCategory> {
    const res = await axiosInstance.put<QuestionCategory>(
      QUESTION_BANK_ENDPOINTS.CATEGORY_DETAIL(courseId, categoryId),
      payload,
    );
    return res.data;
  },

  async deleteCategory(courseId: number, categoryId: number): Promise<void> {
    await axiosInstance.delete(
      QUESTION_BANK_ENDPOINTS.CATEGORY_DETAIL(courseId, categoryId),
    );
  },

  // ── questions ───────────────────────────────────────────────────
  async listQuestions(
    courseId: number,
    filters: QuestionListFilters = {},
  ): Promise<PaginatedQuestions> {
    const res = await axiosInstance.get<PaginatedQuestions>(
      QUESTION_BANK_ENDPOINTS.QUESTIONS(courseId),
      { params: buildParams(filters) },
    );
    return res.data;
  },

  async getQuestion(courseId: number, questionId: number): Promise<Question> {
    const res = await axiosInstance.get<Question>(
      QUESTION_BANK_ENDPOINTS.QUESTION_DETAIL(courseId, questionId),
    );
    return res.data;
  },

  async createQuestion(courseId: number, payload: QuestionCreate): Promise<Question> {
    const res = await axiosInstance.post<Question>(
      QUESTION_BANK_ENDPOINTS.QUESTIONS(courseId),
      payload,
    );
    return res.data;
  },

  async importQuestions(
    courseId: number,
    questions: QuestionCreate[],
  ): Promise<{ created: number; failed: number; errors: string[] }> {
    const res = await axiosInstance.post(
      QUESTION_BANK_ENDPOINTS.QUESTIONS_IMPORT(courseId),
      { questions },
    );
    return res.data;
  },

  async updateQuestion(
    courseId: number,
    questionId: number,
    payload: QuestionUpdate,
  ): Promise<Question> {
    const res = await axiosInstance.put<Question>(
      QUESTION_BANK_ENDPOINTS.QUESTION_DETAIL(courseId, questionId),
      payload,
    );
    return res.data;
  },

  /** Soft delete → Trash. Throws (409) if the question is used by an exam. */
  async deleteQuestion(courseId: number, questionId: number): Promise<void> {
    await axiosInstance.delete(
      QUESTION_BANK_ENDPOINTS.QUESTION_DETAIL(courseId, questionId),
    );
  },

  /** Random-draw quizzes that would be short of questions without this one. */
  async drawImpact(courseId: number, questionId: number): Promise<DrawShortfall[]> {
    const res = await axiosInstance.get<{ quizzes: DrawShortfall[] }>(
      `${QUESTION_BANK_ENDPOINTS.QUESTION_DETAIL(courseId, questionId)}/draw-impact`,
    );
    return res.data.quizzes;
  },

  /** Random-draw quizzes a change to these questions would leave short. */
  async drawImpactFor(courseId: number, questionIds: number[], change: DrawImpactChange): Promise<DrawShortfall[]> {
    const res = await axiosInstance.post<{ quizzes: DrawShortfall[] }>(
      QUESTION_BANK_ENDPOINTS.DRAW_IMPACT(courseId),
      { question_ids: questionIds, ...change },
    );
    return res.data.quizzes;
  },

  async restoreQuestion(courseId: number, questionId: number): Promise<void> {
    await axiosInstance.post(
      QUESTION_BANK_ENDPOINTS.QUESTION_RESTORE(courseId, questionId),
    );
  },

  async purgeQuestion(courseId: number, questionId: number): Promise<void> {
    await axiosInstance.delete(
      QUESTION_BANK_ENDPOINTS.QUESTION_PURGE(courseId, questionId),
    );
  },
};

export const ExamApi = {
  async getExam(courseId: number, activityId: number): Promise<Exam> {
    const res = await axiosInstance.get<Exam>(EXAM_ENDPOINTS.EXAM(courseId, activityId));
    return res.data;
  },

  async updateExam(courseId: number, activityId: number, payload: ExamUpdate): Promise<Exam> {
    const res = await axiosInstance.put<Exam>(
      EXAM_ENDPOINTS.EXAM(courseId, activityId),
      payload,
    );
    return res.data;
  },

  async getResults(courseId: number, activityId: number): Promise<ExamResultsList> {
    const res = await axiosInstance.get<ExamResultsList>(
      EXAM_ENDPOINTS.RESULTS(courseId, activityId),
    );
    return res.data;
  },

  async getAttemptDetail(
    courseId: number,
    activityId: number,
    attemptId: number,
  ): Promise<AttemptDetail> {
    const res = await axiosInstance.get<AttemptDetail>(
      EXAM_ENDPOINTS.ATTEMPT_DETAIL(courseId, activityId, attemptId),
    );
    return res.data;
  },

  async getMarkers(courseId: number): Promise<CourseMarker[]> {
    const res = await axiosInstance.get<CourseMarker[]>(EXAM_ENDPOINTS.MARKERS(courseId));
    return res.data;
  },
};

export const MarkingApi = {
  // Sidebar, TopBar and TasksWidget each ask for this on page load — share one
  // in-flight request (only while pending, so refetches after grading are fresh).
  getQueue(): Promise<MarkingItem[]> {
    return sharedRequest('marking:queue', async () => {
      const res = await axiosInstance.get<MarkingItem[]>(MARKING_ENDPOINTS.QUEUE);
      return res.data;
    });
  },

  async grade(
    responseId: number,
    points: number,
    comment: string | null,
  ): Promise<{ graded: boolean; finalized: boolean }> {
    const res = await axiosInstance.post(MARKING_ENDPOINTS.GRADE(responseId), {
      points,
      comment,
    });
    return res.data;
  },
};

export const ExamTakeApi = {
  async getInfo(courseId: number, activityId: number): Promise<ExamInfo> {
    const res = await axiosInstance.get<ExamInfo>(EXAM_ENDPOINTS.INFO(courseId, activityId));
    return res.data;
  },

  async start(courseId: number, activityId: number, password?: string): Promise<ExamTakeState> {
    const res = await axiosInstance.post<ExamTakeState>(
      EXAM_ENDPOINTS.START(courseId, activityId),
      { password: password ?? null },
    );
    return res.data;
  },

  async getActive(courseId: number, activityId: number): Promise<ExamTakeState | null> {
    const res = await axiosInstance.get(EXAM_ENDPOINTS.ACTIVE(courseId, activityId));
    return res.data && res.data.attempt_id ? (res.data as ExamTakeState) : null;
  },

  async saveResponse(
    courseId: number,
    activityId: number,
    attemptId: number,
    questionId: number,
    response: Record<string, any> | null,
    markedForReview?: boolean,
  ): Promise<void> {
    await axiosInstance.put(
      EXAM_ENDPOINTS.RESPONSE(courseId, activityId, attemptId, questionId),
      { response, marked_for_review: markedForReview ?? null },
    );
  },

  async check(
    courseId: number,
    activityId: number,
    attemptId: number,
    questionId: number,
  ): Promise<{ is_correct: boolean | null; correct_option_ids: number[] }> {
    const res = await axiosInstance.post(
      EXAM_ENDPOINTS.CHECK(courseId, activityId, attemptId, questionId),
    );
    return res.data;
  },

  async submit(
    courseId: number,
    activityId: number,
    attemptId: number,
    timeSpentSeconds: number,
    /** The timer reached zero (not a Submit click): the quiz's time-expiry rule applies. */
    timeup = false,
  ): Promise<ExamResult> {
    const res = await axiosInstance.post<ExamResult>(
      EXAM_ENDPOINTS.SUBMIT(courseId, activityId, attemptId),
      { time_spent_seconds: timeSpentSeconds, timeup },
    );
    return res.data;
  },

  /** Learner's own per-question review (post-submit, reveal-policy aware). */
  async getReview(
    courseId: number,
    activityId: number,
    attemptId: number,
  ): Promise<AttemptDetail> {
    const res = await axiosInstance.get<AttemptDetail>(
      EXAM_ENDPOINTS.ATTEMPT_REVIEW(courseId, activityId, attemptId),
    );
    return res.data;
  },
};
